import * as THREE from 'three';

type Spark = {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  life: number;
  maxLife: number;
};

const LIFE = 0.25;

/** Brief emissive spark burst on bullet impacts against builds/world. */
export class FpsImpactSparks {
  private readonly sparks: Spark[] = [];
  private readonly geo = new THREE.SphereGeometry(0.028, 4, 4);
  private readonly colors = [0x66ffff, 0xffcc44, 0xffee88, 0x88ddff];

  constructor(private readonly scene: THREE.Scene) {}

  spawn(x: number, y: number, z: number, normal?: THREE.Vector3): void {
    const n =
      normal && normal.lengthSq() > 1e-6
        ? normal.clone().normalize()
        : new THREE.Vector3(0, 1, 0);
    const tangent = new THREE.Vector3(0, 1, 0).cross(n);
    if (tangent.lengthSq() < 1e-6) tangent.set(1, 0, 0);
    tangent.normalize();
    const bitangent = new THREE.Vector3().crossVectors(n, tangent).normalize();

    const count = 8 + Math.floor(Math.random() * 5);
    for (let i = 0; i < count; i++) {
      const mat = new THREE.MeshBasicMaterial({
        color: this.colors[i % this.colors.length]!,
        transparent: true,
        opacity: 0.95,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const mesh = new THREE.Mesh(this.geo, mat);
      mesh.position.set(
        x + (Math.random() - 0.5) * 0.06,
        y + (Math.random() - 0.5) * 0.06,
        z + (Math.random() - 0.5) * 0.06,
      );
      mesh.scale.setScalar(0.65 + Math.random() * 0.55);
      this.scene.add(mesh);

      const spread = 0.35 + Math.random() * 0.55;
      const vel = n
        .clone()
        .multiplyScalar(1.2 + Math.random() * 1.8)
        .add(tangent.clone().multiplyScalar((Math.random() - 0.5) * spread))
        .add(bitangent.clone().multiplyScalar((Math.random() - 0.5) * spread));
      vel.y += 0.15 + Math.random() * 0.35;

      this.sparks.push({ mesh, vel, life: 0, maxLife: LIFE * (0.85 + Math.random() * 0.3) });
    }
  }

  update(dt: number): void {
    for (let i = this.sparks.length - 1; i >= 0; i--) {
      const s = this.sparks[i]!;
      s.life += dt;
      s.mesh.position.addScaledVector(s.vel, dt);
      s.vel.y -= dt * 4.8;
      s.vel.multiplyScalar(1 - dt * 2.8);
      const t = s.life / s.maxLife;
      const mat = s.mesh.material as THREE.MeshBasicMaterial;
      mat.opacity = Math.max(0, 0.95 * (1 - t * t));
      s.mesh.scale.setScalar((0.65 + t * 0.15) * (1 - t * 0.35));
      if (s.life >= s.maxLife) {
        this.scene.remove(s.mesh);
        mat.dispose();
        this.sparks.splice(i, 1);
      }
    }
  }

  dispose(): void {
    for (const s of this.sparks) {
      this.scene.remove(s.mesh);
      (s.mesh.material as THREE.Material).dispose();
    }
    this.sparks.length = 0;
    this.geo.dispose();
  }
}
